import { useState, useEffect, useCallback } from 'react';
import assetService from '../services/assetService';

/**
 * Custom hook for managing design assets with robust fallback
 * Handles asset loading, caching, and error recovery
 */
export const useDesignAssets = () => {
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});

  // Get backend URL from service
  const getBackendUrl = useCallback(() => {
    return assetService.getBackendUrl();
  }, []);

  /**
   * Load a specific design asset
   * @param {string} assetName - Name of the asset (e.g., 'tamara-banner')
   * @param {object} options - Loading options
   */
  const loadAsset = useCallback(async (assetName, options = {}) => {
    // Get asset configuration
    const config = assetService.getAssetConfig(assetName);
    const { 
      enableCache = config.cache, 
      timeout = config.timeout,
      fallbackContent = null 
    } = options;

    // Check cache first
    if (enableCache && assets[assetName] && !errors[assetName]) {
      console.log(`✅ Asset '${assetName}' served from cache`);
      return assets[assetName];
    }

    setLoading(prev => ({ ...prev, [assetName]: true }));
    setErrors(prev => ({ ...prev, [assetName]: null }));

    const assetUrl = assetService.getAssetUrl(assetName);
    
    if (!assetUrl) {
      const error = 'Backend URL not configured';
      setErrors(prev => ({ ...prev, [assetName]: error }));
      setLoading(prev => ({ ...prev, [assetName]: false }));
      
      // Return generated fallback if no custom fallback provided
      return fallbackContent || assetService.generateFallbackContent(assetName);
    }

    try {
      console.log(`🔄 Loading asset: ${assetName} from ${assetUrl}`);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(assetUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'Cache-Control': 'public, max-age=3600'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const content = await response.text();
      
      // Validate content is not empty
      if (!content || content.length < 100) {
        throw new Error('Asset content is too short or empty');
      }

      // Store in cache
      setAssets(prev => ({ ...prev, [assetName]: content }));
      setErrors(prev => ({ ...prev, [assetName]: null }));
      
      console.log(`✅ Asset '${assetName}' loaded successfully (${content.length} chars)`);
      return content;

    } catch (error) {
      const errorMessage = error.name === 'AbortError' 
        ? `Timeout loading asset '${assetName}' (${timeout}ms)`
        : `Failed to load asset '${assetName}': ${error.message}`;
      
      console.error(`❌ ${errorMessage}`);
      setErrors(prev => ({ ...prev, [assetName]: errorMessage }));
      
      // Return fallback content on error
      return fallbackContent || assetService.generateFallbackContent(assetName);
    } finally {
      setLoading(prev => ({ ...prev, [assetName]: false }));
    }
  }, [assets, errors]);

  /**
   * Get asset URL for iframe or link usage
   * @param {string} assetName - Name of the asset
   */
  const getAssetUrl = useCallback((assetName) => {
    return assetService.getAssetUrl(assetName);
  }, []);

  /**
   * Get download URL for asset
   * @param {string} assetName - Name of the asset
   */
  const getDownloadUrl = useCallback((assetName) => {
    return assetService.getDownloadUrl(assetName);
  }, []);

  /**
   * Clear cache for specific asset or all assets
   * @param {string} assetName - Optional asset name to clear, clears all if not provided
   */
  const clearCache = useCallback((assetName = null) => {
    if (assetName) {
      setAssets(prev => {
        const newAssets = { ...prev };
        delete newAssets[assetName];
        return newAssets;
      });
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[assetName];
        return newErrors;
      });
      console.log(`🗑️  Cache cleared for asset: ${assetName}`);
    } else {
      setAssets({});
      setErrors({});
      console.log('🗑️  All asset cache cleared');
    }
  }, []);

  /**
   * Check if asset is available (cached and no errors)
   */
  const isAssetAvailable = useCallback((assetName) => {
    return !!(assets[assetName] && !errors[assetName] && !loading[assetName]);
  }, [assets, errors, loading]);

  /**
   * Get asset status
   */
  const getAssetStatus = useCallback((assetName) => {
    if (loading[assetName]) return 'loading';
    if (errors[assetName]) return 'error';
    if (assets[assetName]) return 'ready';
    return 'not_loaded';
  }, [assets, errors, loading]);

  return {
    // Data
    assets,
    loading,
    errors,
    
    // Methods
    loadAsset,
    getAssetUrl,
    getDownloadUrl,
    clearCache,
    isAssetAvailable,
    getAssetStatus,
    
    // Utils
    backendUrl: getBackendUrl(),
    assetService,
    
    // Test methods
    testAsset: assetService.testAssetAvailability,
    testAllAssets: assetService.testAllAssets
  };
};

export default useDesignAssets;